import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label, TextArea } from "@/components/ui/Input";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { formatTimestamp } from "@/lib/utils";

const DATA_CONNECTORS_READ_ONLY = "Your role has read-only access to the Data connectors hub module.";

const QUERY_KEY = "data-control-registry" as const;

const DATA_CONTROL_REGISTRY_HELP =
  "This is the integrator data control registry: for each ingest entity it records which system of record owns each field key on the snapshot records the pipeline uses for live ingest, reconciliation, downstream sync, and broadcast. " +
  "That ownership drives drift handling, Findings, and how unexpected top-level keys are reported. Add or edit rows when a new persisted or snapshot key appears or when responsibility moves; ingest_entity must match an Ingest control row. " +
  "Line fields inside the readCurrent items (or work_order bom_lines) JSON are registered as item.<columnName>, not as a single items row. " +
  "List real header columns on the mirrored public table, other top-level readCurrent aliases, or item.* line keys—not enrich-only properties whose names begin with sweep_ (the hub always treats those as synthetic and they are not stored here). " +
  "If controlled_by is supabase, Supabase is authoritative for that key (for example surrogate id or hub-only columns). " +
  "Other values name external systems of record (megaventory, erpnext, mrp, quickbooks, freshsales, other). " +
  "Rows live in workflow.ingest_sweep_row_registry (table name is historical). The hub loads them whenever it prepares ingest comparisons; updates apply on the next ingest run (per-run cache).";

const NOTES_PREVIEW_LEN = 72;

/** Older databases may still store migration-era "110 seed …" copy; normalize for display only. */
function humanizeRegistryNoteForDisplay(notes: string | null): string {
  if (notes == null || notes.trim() === "") {
    return "";
  }
  let display = notes;
  if (display.includes("110 seed")) {
    display = display
      .replace(
        "110 seed from information_schema.columns ",
        "Seeded from database columns on ",
      )
      .replace(
        "110 seed mrp.inventory_snapshot full catalog",
        "Seeded from mrp.inventory_snapshot column list (initial data control registry, full catalog).",
      )
      .replace("110 seed supplier.data", "Seeded from supplier.data column list (initial data control registry).")
      .replace("110 seed public.", "Seeded from public.")
      .replace(/110 seed /g, "Initial registry note: ");
  }
  return display;
}

function NotesCell({ notes }: { notes: string | null }) {
  const full = humanizeRegistryNoteForDisplay(notes);
  if (full === "") {
    return <span className="text-slate-400">—</span>;
  }
  if (full.length <= NOTES_PREVIEW_LEN) {
    return <span className="text-slate-700">{full}</span>;
  }
  const truncated = `${full.slice(0, NOTES_PREVIEW_LEN - 1)}…`;
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <span className="block max-w-[18rem] cursor-default truncate text-slate-700" tabIndex={0}>
          {truncated}
        </span>
      }
    >
      <span className="max-w-md whitespace-pre-wrap text-xs text-slate-800">{full}</span>
    </HoverHintTrigger>
  );
}

interface RegistryRow {
  ingest_entity: string;
  row_key: string;
  controlled_by: string;
  notes: string | null;
  updated_at: string;
}

interface RegistryListResponse {
  rows: RegistryRow[];
  ingest_entities: string[];
  controlled_by_options: string[];
}

/** Stable key for the row being inline-edited (field keys are unlikely to contain U+001F). */
function registryEditKey(row: Pick<RegistryRow, "ingest_entity" | "row_key">): string {
  return `${row.ingest_entity}\u001f${row.row_key}`;
}

function RegistryTableRow({
  row,
  hubWritesAllowed,
  editing,
  draftControlledBy,
  setDraftControlledBy,
  draftNotes,
  setDraftNotes,
  controlledByOptions,
  editDirty,
  onStartEdit,
  onCancelEdit,
  onSave,
  savePending,
  patchErrorMessage,
  onRemoveRequest,
}: {
  row: RegistryRow;
  hubWritesAllowed: boolean;
  editing: boolean;
  draftControlledBy: string;
  setDraftControlledBy: (value: string) => void;
  draftNotes: string;
  setDraftNotes: (value: string) => void;
  controlledByOptions: string[];
  editDirty: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  savePending: boolean;
  patchErrorMessage: string | null;
  onRemoveRequest: (target: RegistryRow) => void;
}) {
  return (
    <tr className="align-top">
      <td className="px-4 py-2 align-middle">
        <Link
          to={`/erp/entity/${encodeURIComponent(row.ingest_entity)}`}
          className="font-medium text-brand-700 hover:underline"
        >
          {row.ingest_entity}
        </Link>
      </td>
      <td className="px-4 py-2 align-middle font-mono text-xs text-slate-800">{row.row_key}</td>
      <td className="px-4 py-2 align-middle">
        {editing ? (
          <select
            className="h-8 w-full max-w-[11rem] rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            value={draftControlledBy}
            onChange={(event) => setDraftControlledBy(event.target.value)}
            disabled={!hubWritesAllowed}
            aria-label={`Controlled by for ${row.ingest_entity} ${row.row_key}`}
          >
            {controlledByOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-slate-800">{row.controlled_by}</span>
        )}
      </td>
      <td className="px-4 py-2 align-middle text-sm">
        {editing ? (
          <TextArea
            value={draftNotes}
            onChange={(event) => setDraftNotes(event.target.value)}
            rows={2}
            className="max-w-md text-xs"
            disabled={!hubWritesAllowed}
            aria-label={`Notes for ${row.ingest_entity} ${row.row_key}`}
          />
        ) : (
          <NotesCell notes={row.notes} />
        )}
      </td>
      <td className="px-4 py-2 align-middle text-xs text-slate-600 whitespace-nowrap">
        {formatTimestamp(row.updated_at)}
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap">
        {editing ? (
          <div>
            <div className="inline-flex flex-nowrap items-center justify-end gap-2">
              <Button type="button" size="sm" variant="secondary" className="shrink-0" onClick={onCancelEdit}>
                Cancel
              </Button>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="inline">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  className="shrink-0"
                  disabled={!hubWritesAllowed || !editDirty || savePending}
                  isLoading={savePending}
                  onClick={onSave}
                >
                  Save
                </Button>
              </ReadOnlyHoverLayer>
            </div>
            {patchErrorMessage != null && patchErrorMessage !== "" ? (
              <p className="mt-1 max-w-[14rem] text-xs text-rose-600">{patchErrorMessage}</p>
            ) : null}
          </div>
        ) : (
          <div className="inline-flex flex-nowrap items-center gap-2">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="inline">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="shrink-0"
                disabled={!hubWritesAllowed}
                onClick={onStartEdit}
              >
                Edit
              </Button>
            </ReadOnlyHoverLayer>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="inline">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="shrink-0 text-rose-700 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800"
                disabled={!hubWritesAllowed}
                onClick={() => onRemoveRequest(row)}
              >
                Remove
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        )}
      </td>
    </tr>
  );
}

export function DataControlRegistryPage() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("erp");
  const [entityFilter, setEntityFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftControlledBy, setDraftControlledBy] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [removeTarget, setRemoveTarget] = useState<RegistryRow | null>(null);

  const [newEntity, setNewEntity] = useState("");
  const [newRowKey, setNewRowKey] = useState("");
  const [newControlledBy, setNewControlledBy] = useState("megaventory");
  const [newNotes, setNewNotes] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: [QUERY_KEY, entityFilter],
    queryFn: () =>
      apiFetch<RegistryListResponse>("/api/ingest-sweep-row-registry", {
        query: entityFilter === "" ? undefined : { ingest_entity: entityFilter },
      }),
  });

  const controlledByOptions = listQuery.data?.controlled_by_options ?? [
    "megaventory",
    "erpnext",
    "quickbooks",
    "supabase",
    "freshsales",
    "mrp",
    "other",
  ];

  const postMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ row: RegistryRow }>("/api/ingest-sweep-row-registry", {
        method: "POST",
        body: {
          ingest_entity: newEntity.trim(),
          row_key: newRowKey.trim(),
          controlled_by: newControlledBy,
          notes: newNotes.trim() === "" ? null : newNotes.trim(),
        },
      }),
    onSuccess: () => {
      setNewRowKey("");
      setNewNotes("");
      setAddError(null);
      setAddOpen(false);
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error) => {
      setAddError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const patchMutation = useMutation({
    mutationFn: (body: { ingest_entity: string; row_key: string; controlled_by: string; notes: string }) =>
      apiFetch<{ row: RegistryRow }>("/api/ingest-sweep-row-registry", {
        method: "PATCH",
        body: {
          ingest_entity: body.ingest_entity,
          row_key: body.row_key,
          controlled_by: body.controlled_by,
          notes: body.notes,
        },
      }),
    onSuccess: () => {
      setEditingKey(null);
      patchMutation.reset();
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (target: RegistryRow) =>
      apiFetch<{ deleted: { ingest_entity: string; row_key: string } }>("/api/ingest-sweep-row-registry", {
        method: "DELETE",
        query: { ingest_entity: target.ingest_entity, row_key: target.row_key },
      }),
    onSuccess: () => {
      setRemoveTarget(null);
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const entityOptions = useMemo(() => listQuery.data?.ingest_entities ?? [], [listQuery.data?.ingest_entities]);

  const editingRowSnapshot = useMemo(() => {
    if (editingKey == null || listQuery.data?.rows == null) {
      return null;
    }
    return listQuery.data.rows.find((r) => registryEditKey(r) === editingKey) ?? null;
  }, [editingKey, listQuery.data?.rows]);

  const editDirty = useMemo(() => {
    if (editingRowSnapshot == null) {
      return false;
    }
    return (
      draftControlledBy !== editingRowSnapshot.controlled_by ||
      draftNotes !== (editingRowSnapshot.notes ?? "")
    );
  }, [editingRowSnapshot, draftControlledBy, draftNotes]);

  const cancelInlineEdit = useCallback(() => {
    patchMutation.reset();
    setEditingKey(null);
  }, [patchMutation]);

  const startInlineEdit = useCallback(
    (row: RegistryRow) => {
      patchMutation.reset();
      setEditingKey(registryEditKey(row));
      setDraftControlledBy(row.controlled_by);
      setDraftNotes(row.notes ?? "");
    },
    [patchMutation],
  );

  useEffect(() => {
    if (!addOpen && editingKey == null && removeTarget == null) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        postMutation.reset();
        setAddError(null);
        setAddOpen(false);
        patchMutation.reset();
        setEditingKey(null);
        deleteMutation.reset();
        setRemoveTarget(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [addOpen, editingKey, removeTarget, postMutation, patchMutation, deleteMutation]);

  const openAddModal = (): void => {
    patchMutation.reset();
    setEditingKey(null);
    postMutation.reset();
    setNewEntity("");
    setNewRowKey("");
    setNewControlledBy("megaventory");
    setNewNotes("");
    setAddError(null);
    setAddOpen(true);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PageTitleWithHelp hoverHint={DATA_CONTROL_REGISTRY_HELP}>Data control registry</PageTitleWithHelp>
        <div className="flex shrink-0 flex-col items-stretch gap-3 sm:items-end">
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="inline">
            <Button
              type="button"
              className="self-start sm:self-end"
              size="sm"
              variant="primary"
              disabled={!hubWritesAllowed}
              onClick={openAddModal}
            >
              <Plus className="h-4 w-4" />
              Add row
            </Button>
          </ReadOnlyHoverLayer>
          <label className="block text-left sm:text-right">
            <span className="block text-xs font-medium text-slate-600">Filter by entity</span>
            <select
              className="mt-1 h-9 min-w-[12rem] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              value={entityFilter}
              onChange={(event) => setEntityFilter(event.target.value)}
            >
              <option value="">All entities</option>
              {entityOptions.map((entity) => (
                <option key={entity} value={entity}>
                  {entity}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {addOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => {
            postMutation.reset();
            setAddError(null);
            setAddOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="data-control-add-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="data-control-add-title" className="text-lg font-semibold text-slate-900">
                  Add registry row
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Ingest entity must match an Ingest control row. Field key is the JSON property on the compared
                  record (including synthetic keys such as <code className="rounded bg-slate-100 px-0.5">items</code>
                  ).
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 text-slate-500 hover:text-slate-900"
                aria-label="Close dialog"
                onClick={() => {
                  postMutation.reset();
                  setAddError(null);
                  setAddOpen(false);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="block">
              <div className="mt-4 space-y-3">
                <div>
                  <Label htmlFor="data-control-add-entity">Ingest entity</Label>
                  <select
                    id="data-control-add-entity"
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={newEntity}
                    onChange={(event) => setNewEntity(event.target.value)}
                    disabled={!hubWritesAllowed || entityOptions.length === 0}
                  >
                    <option value="">{entityOptions.length === 0 ? "Loading entities…" : "Select entity…"}</option>
                    {entityOptions.map((entity) => (
                      <option key={entity} value={entity}>
                        {entity}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="data-control-add-key">Field key</Label>
                  <input
                    id="data-control-add-key"
                    type="text"
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={newRowKey}
                    onChange={(event) => setNewRowKey(event.target.value)}
                    disabled={!hubWritesAllowed}
                    placeholder="e.g. expected_date"
                  />
                </div>
                <div>
                  <Label htmlFor="data-control-add-owner">Controlled by</Label>
                  <select
                    id="data-control-add-owner"
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={newControlledBy}
                    onChange={(event) => setNewControlledBy(event.target.value)}
                    disabled={!hubWritesAllowed}
                  >
                    {controlledByOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="data-control-add-notes">Notes (optional)</Label>
                  <TextArea
                    id="data-control-add-notes"
                    value={newNotes}
                    onChange={(event) => setNewNotes(event.target.value)}
                    rows={3}
                    disabled={!hubWritesAllowed}
                  />
                </div>
                {addError ? <p className="text-sm text-rose-600">{addError}</p> : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    variant="primary"
                    disabled={
                      !hubWritesAllowed ||
                      newEntity.trim() === "" ||
                      newRowKey.trim() === "" ||
                      postMutation.isPending
                    }
                    isLoading={postMutation.isPending}
                    onClick={() => postMutation.mutate()}
                  >
                    Add row
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={postMutation.isPending}
                    onClick={() => {
                      postMutation.reset();
                      setAddError(null);
                      setAddOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </ReadOnlyHoverLayer>
          </div>
        </div>
      ) : null}

      {removeTarget != null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => {
            deleteMutation.reset();
            setRemoveTarget(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="data-control-remove-title"
            className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="data-control-remove-title" className="text-lg font-semibold text-slate-900">
              Remove registry entry?
            </h2>
            <p className="mt-2 font-mono text-sm text-slate-800">
              {removeTarget.ingest_entity} · {removeTarget.row_key}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              This deletes the row from the data control registry. You cannot remove the last entry for an ingest
              entity.
            </p>
            {deleteMutation.isError ? (
              <p className="mt-3 text-sm text-rose-600">
                {deleteMutation.error instanceof ApiError
                  ? deleteMutation.error.message
                  : String(deleteMutation.error)}
              </p>
            ) : null}
            <div className="mt-5 inline-flex flex-nowrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  deleteMutation.reset();
                  setRemoveTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={deleteMutation.isPending}
                isLoading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(removeTarget)}
              >
                Remove entry
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Registry entries</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {listQuery.isError ? (
            <p className="px-4 py-4 text-sm text-rose-600">
              {listQuery.error instanceof ApiError ? listQuery.error.message : String(listQuery.error)}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Entity</th>
                    <th className="px-4 py-2 font-medium">Field key</th>
                    <th className="px-4 py-2 font-medium">Controlled by</th>
                    <th className="px-4 py-2 font-medium">Notes</th>
                    <th className="px-4 py-2 font-medium">Updated</th>
                    <th className="px-4 py-2 font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {listQuery.isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        Loading…
                      </td>
                    </tr>
                  ) : listQuery.data?.rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No entries match this filter.
                      </td>
                    </tr>
                  ) : (
                    listQuery.data?.rows.map((row) => {
                      const rowKey = registryEditKey(row);
                      const editing = editingKey === rowKey;
                      const patchErrorMessage =
                        editing && patchMutation.isError
                          ? patchMutation.error instanceof ApiError
                            ? patchMutation.error.message
                            : String(patchMutation.error)
                          : null;
                      return (
                        <RegistryTableRow
                          key={rowKey}
                          row={row}
                          hubWritesAllowed={hubWritesAllowed}
                          editing={editing}
                          draftControlledBy={draftControlledBy}
                          setDraftControlledBy={setDraftControlledBy}
                          draftNotes={draftNotes}
                          setDraftNotes={setDraftNotes}
                          controlledByOptions={controlledByOptions}
                          editDirty={editDirty}
                          onStartEdit={() => startInlineEdit(row)}
                          onCancelEdit={cancelInlineEdit}
                          onSave={() =>
                            patchMutation.mutate({
                              ingest_entity: row.ingest_entity,
                              row_key: row.row_key,
                              controlled_by: draftControlledBy,
                              notes: draftNotes,
                            })
                          }
                          savePending={patchMutation.isPending}
                          patchErrorMessage={patchErrorMessage}
                          onRemoveRequest={setRemoveTarget}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
